import { dspLabel } from '../../ReportingV2/format';

/**
 * Canonical metric catalog — ported from flank-20 (Martin's field-spec metric
 * list), adapted to the flank / ribeye-api reporting stack. Widget metric
 * pickers read from CANONICAL_METRICS so adding a metric is a one-file change.
 *
 * `key` is the ribeye-api reporting metric id sent to postReport; `format`
 * drives metricFormat.ts rendering. `availability` lets pickers honestly badge
 * metrics that aren't ingested into canonical yet (see the field-availability
 * matrix — store_visits / leads / roas etc. have no source today).
 */
export type MetricFormat =
  | 'count' // integer with K/M abbreviation
  | 'currency' // USD
  | 'percent' // 0-1 rendered as %
  | 'ratio' // e.g. frequency 3.4
  | 'multiple'; // e.g. ROAS 2.5×

export type MetricAvailability = 'ga' | 'partial' | 'beeswax_only' | 'coming_soon';

export interface CanonicalMetricDef {
  key: string;
  label: string;
  format: MetricFormat;
  group: 'delivery' | 'engagement' | 'video' | 'conversion' | 'reach' | 'efficiency' | 'goal';
  higherIsBetter?: boolean;
  /** Defaults to "ga". Widgets should badge non-ga metrics in pickers. */
  availability?: MetricAvailability;
  sourceNote?: string;
  derivedFrom?: string;
}

export const CANONICAL_METRICS: CanonicalMetricDef[] = [
  // Delivery
  { key: 'impressions', label: 'Impressions', format: 'count', group: 'delivery', higherIsBetter: true },
  { key: 'spend', label: 'Spend', format: 'currency', group: 'delivery' },
  // What this whitelabel is billed, set per line item in Directus — distinct
  // from wholesale Spend above and gated by its own permission (#4446), not
  // "Show wholesale Spend and CPM" (flank#5054).
  {
    key: 'client_spend',
    label: 'Client Spend',
    format: 'currency',
    group: 'delivery',
    sourceNote: 'Blank where no sold CPM has been entered for this selection, never $0.',
  },
  // Engagement
  { key: 'clicks', label: 'Clicks', format: 'count', group: 'engagement', higherIsBetter: true },
  {
    key: 'ctr',
    label: 'CTR',
    format: 'percent',
    group: 'engagement',
    higherIsBetter: true,
    derivedFrom: 'clicks / impressions',
  },
  // Video
  { key: 'video_completes', label: 'Video Completes', format: 'count', group: 'video', higherIsBetter: true },
  {
    key: 'vcr',
    label: 'VCR',
    format: 'percent',
    group: 'video',
    higherIsBetter: true,
    derivedFrom: 'video_completes / video_views',
  },
  // Conversion
  {
    key: 'conversions',
    label: 'Conversions',
    format: 'count',
    group: 'conversion',
    higherIsBetter: true,
    sourceNote: 'Available in the performance report.',
  },
  {
    key: 'store_visits',
    label: 'Store Visits',
    format: 'count',
    group: 'conversion',
    higherIsBetter: true,
    availability: 'coming_soon',
    sourceNote: 'No measurement partner delivering store-visit data today.',
  },
  {
    key: 'leads',
    label: 'Leads',
    format: 'count',
    group: 'conversion',
    higherIsBetter: true,
    availability: 'coming_soon',
  },
  // Reach
  {
    key: 'reach',
    label: 'Reach',
    format: 'count',
    group: 'reach',
    higherIsBetter: true,
    sourceNote: 'Distinct users; equivalent to ip_reach today.',
  },
  { key: 'frequency', label: 'Frequency', format: 'ratio', group: 'reach', derivedFrom: 'impressions / reach' },
  // Efficiency
  {
    key: 'client_cpm',
    label: 'Client CPM',
    format: 'currency',
    group: 'efficiency',
    derivedFrom: 'client_spend / impressions * 1000 (null where client_spend is null)',
  },
  {
    key: 'cpc',
    label: 'CPC',
    format: 'currency',
    group: 'efficiency',
    derivedFrom: 'spend / clicks (null when clicks = 0)',
  },
  // Not in SUPPORTED_METRIC_KEYS (flank#5053): no `cpa` ratio metric exists in
  // ribeye-api's metricRegistry.ts, and conversions can't safely be summed
  // client-side across dsps (see CONVERSION_METRIC_KEYS) to derive one here —
  // a backend gap, not a frontend omission. Revisit once the API adds it.
  {
    key: 'cpa',
    label: 'CPA',
    format: 'currency',
    group: 'efficiency',
    derivedFrom: 'spend / conversions (null when conversions = 0)',
  },
  // Fetched directly as the API's own `cpm` ratio metric (flank#5053), not
  // derived from spend/impressions — the API's denominator counts only
  // impressions that could carry a cost (excludes GAM's sell-side rows, which
  // are structurally spend=NULL), so a naive spend/impressions*1000 here would
  // disagree with Unified Reporting's own CPM card by the same ~40% dilution
  // CPC's own history already caught (ribeye-api metricRegistry.ts `cpm`).
  {
    key: 'cpm',
    label: 'CPM',
    format: 'currency',
    group: 'efficiency',
    derivedFrom: '(priced spend / priced impressions) * 1000',
  },
  // Same backend gap as cpa above: no `cpcv` ratio metric in ribeye-api's
  // metricRegistry.ts, and spend/video_completes risks the same priced-vs-raw
  // dilution cpm/cpc's own history caught — left out of SUPPORTED_METRIC_KEYS
  // rather than shipping a number with nothing to verify it against.
  { key: 'cpcv', label: 'CPCV', format: 'currency', group: 'efficiency', derivedFrom: 'spend / video_completes' },
  {
    key: 'roas',
    label: 'ROAS',
    format: 'multiple',
    group: 'efficiency',
    higherIsBetter: true,
    availability: 'coming_soon',
    sourceNote: 'Requires revenue signal not yet ingested.',
  },
  // Goal
  {
    key: 'kpi_goal',
    label: 'KPI Goal',
    format: 'count',
    group: 'goal',
    availability: 'coming_soon',
    sourceNote: 'Source not decided — line-item goal, pitch goal, or widget target?',
  },
];

export const METRIC_AVAILABILITY_LABELS: Record<MetricAvailability, string> = {
  ga: '',
  partial: 'Partial',
  // Interpolated, not hardcoded (flank#4914, Lucas review) — stays in sync
  // with the display name rather than reading "Beeswax only" after the rename.
  beeswax_only: `${dspLabel('beeswax')} only`,
  coming_soon: 'Coming soon',
};

const CANONICAL_METRIC_INDEX: Record<string, CanonicalMetricDef> = Object.fromEntries(
  CANONICAL_METRICS.map(m => [m.key, m]),
);

/** Metrics wired to the ribeye-api reporting backend today (postReport). cpa
 * and cpcv are deliberately absent (flank#5053) — see their catalog entries
 * above for why. */
export const SUPPORTED_METRIC_KEYS = new Set([
  'impressions',
  'spend',
  'client_spend',
  'client_cpm',
  'clicks',
  'ctr',
  'vcr',
  'conversions',
  'video_completes',
  'reach',
  'frequency',
  'cpm',
  'cpc',
]);

/**
 * Conversion-denominated metrics, gated by the single-DSP rule (see
 * `conversionsVisible` in dashboardFilters.ts). CPA and ROAS are included
 * because they divide by conversions, so they inherit the same double-count.
 */
export const CONVERSION_METRIC_KEYS = new Set(['conversions', 'cpa', 'roas', 'store_visits', 'leads']);

export function isConversionMetric(key: string): boolean {
  return CONVERSION_METRIC_KEYS.has(key);
}

/**
 * "Vanity" metrics in the unified-reporting sense (Prod/Eng office hours,
 * 2026-07-28): rates that are not comparable across channels, so they only
 * surface once a single channel is isolated.
 *
 * CTR and VCR were in this set originally, but Unified Reporting's own
 * headline KPI strip shows both unconditionally — removed here to match it
 * (flank#5052; see `vanityMetricsVisible`'s doc comment in dashboardFilters.ts
 * for why each is actually safe to blend). CPCV only exists on video, so
 * blending it across channels produces a number whose denominator changes
 * per row — still gated.
 */
export const CHANNEL_SPECIFIC_METRIC_KEYS = new Set(['cpcv', 'ltr', 'engagement_rate']);

export function isChannelSpecificMetric(key: string): boolean {
  return CHANNEL_SPECIFIC_METRIC_KEYS.has(key);
}

/**
 * Reach and Frequency, gated by the single-DSP rule (see `reachVisible` in
 * dashboardFilters.ts) — same failure mode as conversions (flank#3971/#4479 in
 * Unified Reporting): each DSP reports its own audience, so summing reach
 * across platforms double-counts anyone reached on more than one, and
 * Frequency (impressions ÷ reach) inherits the same error through its
 * denominator.
 */
export const REACH_METRIC_KEYS = new Set(['reach', 'frequency']);

export function isReachMetric(key: string): boolean {
  return REACH_METRIC_KEYS.has(key);
}

/**
 * Metrics that can't be summed across rows at all — a ratio averages instead,
 * or (for an API-only ratio with no additive components on hand, like CPM/CPC)
 * a client-side recomputation isn't possible and the caller must read it from
 * a dedicated ratio entry instead (see tableTotals.ts). Kept explicit rather
 * than inferred from `format` (CodeRabbit, flank#5065): cpm/cpc/client_cpm are
 * all `format: 'currency'`, same as genuinely additive Spend/Client Spend, so
 * a format-based check summed them across a bucketed week/month the same way
 * it would sum Spend — meaningless for a per-impression/per-click rate, the
 * same category of bug CTR/VCR/Frequency summing already had to be fixed for.
 */
export const NON_ADDITIVE_METRIC_KEYS = new Set(['ctr', 'vcr', 'frequency', 'roas', 'cpm', 'cpc', 'client_cpm']);

export function isNonAdditiveMetric(key: string): boolean {
  return NON_ADDITIVE_METRIC_KEYS.has(key);
}

export function getMetricDef(key: string): CanonicalMetricDef | undefined {
  return CANONICAL_METRIC_INDEX[key];
}

export function isMetricSupported(key: string): boolean {
  return SUPPORTED_METRIC_KEYS.has(key);
}
